'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import SvgTabMoreLeft from '@common/icon/SvgTabMoreLeft';
import SvgTabMoreRight from '@common/icon/SvgTabMoreRight';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { Image } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';
import { useSelectedSnapIndex } from '@/hooks/useSelectedSnapIndex';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { getReviewLabelText, ReviewLabelBadge } from './ReviewLabelBadge';
import { ReviewPopupContent } from './ReviewPopupContent';
import type { GoodsHomeReviewItem } from './ReviewSectionServer';

//* 시안은 PC 2행·모바일 3행으로 쌓은 열을 가로로 넘긴다
const DESKTOP_ROW_COUNT = 2;
const MOBILE_ROW_COUNT = 3;
const toColumns = (reviews: GoodsHomeReviewItem[], rowCount: number) => Array.from({
  length: Math.ceil(reviews.length / rowCount)
}, (_, index) => reviews.slice(index * rowCount, (index + 1) * rowCount));
const ReviewCard = t0 => {
  const $ = _c(27);
  const {
    review
  } = t0;
  let t1;
  if ($[0] !== review.orderType || $[1] !== review.productNumber || $[2] !== review.reviewId) {
    t1 = () => {
      openResponsiveDialog({
        component: ReviewPopupContent,
        modalHeader: getReviewLabelText(review.orderType),
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          reviewId: review.reviewId,
          productNumber: review.productNumber
        },
        drawerNoPadding: true
      });
    };
    $[0] = review.orderType;
    $[1] = review.productNumber;
    $[2] = review.reviewId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleClick = t1;
  const t2 = `${review.productName} 후기 상세 보기`;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group flex w-full items-center gap-12 overflow-hidden rounded-small bg-Gray-25 p-12 text-start", "transition-colors duration-150 hover:bg-Gray-50 active:bg-Gray-100 motion-reduce:transition-none", FOCUS_RING);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== review.fileUrl) {
    t4 = <span className="relative size-72 shrink-0 overflow-hidden rounded-xsmall"><Image src={review.fileUrl} alt="" fill={true} sizes="72px" className="object-cover" /></span>;
    $[5] = review.fileUrl;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== review.orderType) {
    t5 = <ReviewLabelBadge orderType={review.orderType} />;
    $[7] = review.orderType;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== review.rating) {
    t6 = <Rating value={review.rating} size={10} />;
    $[9] = review.rating;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <span className="flex items-center justify-between gap-8">{t5}{t6}</span>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== review.contents) {
    t8 = <span className="line-clamp-2 text-Gray-900 typo-body4">{review.contents}</span>;
    $[14] = review.contents;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== review.productName) {
    t9 = <span className="truncate text-Gray-500 typo-caption1">{review.productName}</span>;
    $[16] = review.productName;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <span className="flex min-w-0 flex-1 flex-col gap-4">{t7}{t8}{t9}</span>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleClick || $[23] !== t10 || $[24] !== t2 || $[25] !== t4) {
    t11 = <button type="button" onClick={handleClick} aria-label={t2} className={t3}>{t4}{t10}</button>;
    $[22] = handleClick;
    $[23] = t10;
    $[24] = t2;
    $[25] = t4;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
const ReviewColumn = t0 => {
  const $ = _c(7);
  const {
    reviews,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex shrink-0 flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reviews) {
    t2 = reviews.map(_temp);
    $[2] = reviews;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className={t1}>{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const ReviewCarousel = t0 => {
  const $ = _c(28);
  const {
    reviews
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t1);
  const selectedIndex = useSelectedSnapIndex(emblaApi);
  let t2;
  if ($[1] !== emblaApi) {
    t2 = emblaApi?.scrollSnapList().length ?? 1;
    $[1] = emblaApi;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const lastSnapIndex = t2 - 1;
  let t3;
  if ($[3] !== reviews) {
    t3 = toColumns(reviews, MOBILE_ROW_COUNT).map(_temp2);
    $[3] = reviews;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <ul aria-label={"\uD6C4\uAE30 \uBAA9\uB85D"} className="hidden snap-x scroll-px-20 gap-8 overflow-x-auto px-20 hide-scrollbar mobile-tablet:flex">{t3}</ul>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== reviews) {
    t5 = toColumns(reviews, DESKTOP_ROW_COUNT).map(_temp3);
    $[7] = reviews;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <ul aria-label={"\uD6C4\uAE30 \uBAA9\uB85D"} className="flex">{t5}</ul>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== emblaRef || $[12] !== t6) {
    t7 = <div ref={emblaRef} className="overflow-hidden">{t6}</div>;
    $[11] = emblaRef;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== emblaApi || $[15] !== selectedIndex) {
    t8 = selectedIndex > 0 ? <Button aria-label={"\uC774\uC804 \uD6C4\uAE30"} theme="sub-text" className="absolute left-0 top-1/2 size-fit -translate-x-1/2 -translate-y-1/2 p-0" onClick={() => emblaApi?.scrollPrev()}><SvgTabMoreLeft className="size-48" originalFill={true} /></Button> : null;
    $[14] = emblaApi;
    $[15] = selectedIndex;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== emblaApi || $[18] !== lastSnapIndex || $[19] !== selectedIndex) {
    t9 = selectedIndex < lastSnapIndex ? <Button aria-label={"\uB2E4\uC74C \uD6C4\uAE30"} theme="sub-text" className="absolute right-0 top-1/2 size-fit -translate-y-1/2 translate-x-1/2 p-0" onClick={() => emblaApi?.scrollNext()}><SvgTabMoreRight className="size-48" originalFill={true} /></Button> : null;
    $[17] = emblaApi;
    $[18] = lastSnapIndex;
    $[19] = selectedIndex;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = <div className="relative mobile-tablet:hidden">{t7}{t8}{t9}</div>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t4) {
    t11 = <div className="w-full px-40 content-container mobile-tablet:px-0">{t4}{t10}</div>;
    $[25] = t10;
    $[26] = t4;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp(review) {
  return <ReviewCard key={review.reviewId} review={review} />;
}
function _temp2(column) {
  return <ReviewColumn key={column[0].reviewId} reviews={column} className="w-290 snap-start gap-8" />;
}
function _temp3(column_0) {
  return <ReviewColumn key={column_0[0].reviewId} reviews={column_0} className="mr-16 w-[24.6875rem] gap-12 last:mr-0" />;
}